import { Alert, Button, Col, Row, Space, Typography } from "antd";
import { Database, Rocket, Code2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import {
	ArticleSchemaCard,
	FAQSchemaCard,
	RecentRunsList,
} from "@/components/SchemaManagement";
import { ContentSelectionModal } from "@/components/SchemaManagement/ContentSelectionModal";
import { SchemaGenerator } from "@/components/SchemaGenerator";
import {
	useRunFullImplementation,
	useSchemaOverview,
} from "@/hooks/useSchemaManagement";
import { useTenantStore } from "@/stores/useTenantStore";
import type { FAQContentType } from "@/types/schema";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const { Title, Paragraph } = Typography;

const SchemaManagementPage: React.FC = () => {
	const { currentTenant } = useTenantStore();
	const { data: overview, error } = useSchemaOverview();
	const runFull = useRunFullImplementation();

	// Modal state
	const [showSelectionModal, setShowSelectionModal] = useState(false);
	const [modalSchemaType, setModalSchemaType] = useState<"article" | "faq">(
		"article",
	);
	const [modalContentType, setModalContentType] =
		useState<FAQContentType>("post");

	// Modal handlers
	const openArticleSelectionModal = () => {
		setModalSchemaType("article");
		setShowSelectionModal(true);
	};

	const openFAQSelectionModal = (contentType: FAQContentType) => {
		setModalSchemaType("faq");
		setModalContentType(contentType);
		setShowSelectionModal(true);
	};

	const handleModalClose = () => {
		setShowSelectionModal(false);
	};

	// Show tenant selection prompt if no tenant selected
	if (!currentTenant) {
		return (
			<div style={{ maxWidth: 600, margin: "40px auto", textAlign: "center" }}>
				<Alert
					type="warning"
					message="No workspace selected"
					description="Please select a workspace from the header to manage schema implementation."
					showIcon
				/>
			</div>
		);
	}

	return (
		<div className="max-w-7xl mx-auto">
			<Tabs defaultValue="generator" className="w-full">
				<TabsList className="mb-6 bg-muted p-1 rounded-lg">
					<TabsTrigger 
						value="generator" 
						className="flex items-center gap-1.5 px-4 py-2 data-[state=active]:bg-card data-[state=active]:shadow-sm rounded-md transition-all"
					>
						<Code2 className="w-4 h-4" />
						Schema Generator
					</TabsTrigger>
					<TabsTrigger 
						value="implementation" 
						className="flex items-center gap-1.5 px-4 py-2 data-[state=active]:bg-card data-[state=active]:shadow-sm rounded-md transition-all"
					>
						<Database className="w-4 h-4" />
						Bulk Implementation
					</TabsTrigger>
				</TabsList>

				{/* Schema Generator Tab */}
				<TabsContent value="generator" className="mt-0">
					<SchemaGenerator />
				</TabsContent>

				{/* Existing Bulk Implementation Tab */}
				<TabsContent value="implementation" className="mt-0">
					<Space direction="vertical" size={24} style={{ width: "100%" }}>
						{/* Header */}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
								flexWrap: "wrap",
								gap: 16,
							}}
						>
							<div>
								<Title
									level={2}
									style={{
										marginBottom: 8,
										display: "flex",
										alignItems: "center",
										gap: 12,
									}}
								>
									<Database size={32} />
									Schema Implementation
								</Title>
								<Paragraph
									type="secondary"
									style={{ marginBottom: 0, maxWidth: 600 }}
								>
									Structured data markup (schema.org) helps search engines
									understand your content better, improving visibility in search
									results and enabling rich snippets.
								</Paragraph>
							</div>

							<Button
								type="primary"
								size="large"
								icon={<Rocket size={18} />}
								onClick={() => runFull.mutate()}
								loading={runFull.isPending}
								disabled={runFull.isPending}
							>
								{runFull.isPending ? "Running All..." : "Run All"}
							</Button>
						</div>

						{/* Error state */}
						{error && (
							<Alert
								type="error"
								message="Failed to Load Schema Overview"
								description={
									error instanceof Error ? error.message : "An unknown error occurred"
								}
								showIcon
							/>
						)}

						{/* Schema Cards Grid */}
						<Row gutter={[24, 24]}>
							<Col xs={24} lg={12}>
								<ArticleSchemaCard
									stats={overview?.article}
									onSelectItems={openArticleSelectionModal}
								/>
							</Col>
							<Col xs={24} lg={12}>
								<FAQSchemaCard
									stats={overview?.faq}
									onSelectItems={openFAQSelectionModal}
								/>
							</Col>
						</Row>

						{/* Recent Runs */}
						<RecentRunsList runs={overview?.recent_runs ?? []} />

						{/* Content Selection Modal */}
						<ContentSelectionModal
							isOpen={showSelectionModal}
							onClose={handleModalClose}
							schemaType={modalSchemaType}
							contentType={modalContentType}
						/>
					</Space>
				</TabsContent>
			</Tabs>
		</div>
	);
};

export default SchemaManagementPage;
